import { Seo } from '@/components/Seo';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield, Database, Eye, Lock, UserCheck, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Logo } from '@/components/Logo';

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

export default function DatenschutzPage() {
  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <Seo
        title="Datenschutzerklärung"
        description="Datenschutzerklärung von marKIpilot: Wie die MYSYMP AG personenbezogene Daten gemäss DSGVO und Schweizer Datenschutzrecht verarbeitet und schützt."
        path="/datenschutz"
      />
      {/* Navigation - with iPhone Safe Area */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-white/5" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <Link to="/" className="flex items-center gap-2">
              <Logo size="small" />
            </Link>
            <Link to="/">
              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Zurück
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Content - with safe area padding */}
      <div style={{ paddingTop: 'calc(env(safe-area-inset-top) + 5rem)', paddingBottom: 'max(4rem, env(safe-area-inset-bottom))' }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <motion.div initial="hidden" animate="visible" variants={fadeInUp}>
            <h1 className="font-display text-4xl sm:text-5xl font-bold mb-4">
              Datenschutzerklärung
            </h1>
            <p className="text-slate-400 mb-8">Zuletzt aktualisiert: Februar 2026</p>

            <div className="space-y-8">
              {/* Intro */}
              <section className="bg-white/5 rounded-2xl border border-white/10 p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-teal-500/20 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-teal-400" />
                  </div>
                  <h2 className="font-display text-xl font-bold">Verantwortliche Stelle</h2>
                </div>
                <p className="text-slate-400 mb-4">
                  Verantwortlich für die Datenverarbeitung auf dieser Website ist:
                </p>
                <div className="bg-slate-900/50 rounded-xl p-4">
                  <p className="text-white font-semibold">MYSYMP AG</p>
                  <p className="text-slate-400">Oberdorfstrasse 18a, 6207 Nottwil, Schweiz</p>
                  <p className="text-slate-400">E-Mail: info@mysymp.ch</p>
                  <p className="text-slate-400">Tel: +41 79 471 06 25</p>
                </div>
              </section>

              {/* Data Collection */}
              <section className="bg-white/5 rounded-2xl border border-white/10 p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                    <Database className="w-5 h-5 text-purple-400" />
                  </div>
                  <h2 className="font-display text-xl font-bold">Erfassung von Daten</h2>
                </div>
                <div className="space-y-4 text-slate-400 text-sm leading-relaxed">
                  <p>
                    <strong className="text-white">Server-Log-Dateien:</strong> Bei jedem Zugriff auf unsere Website werden automatisch Informationen erfasst, die Ihr Browser übermittelt (IP-Adresse, Datum und Uhrzeit, aufgerufene Seite, Browsertyp).
                  </p>
                  <p>
                    <strong className="text-white">Kontaktformular:</strong> Wenn Sie uns über das Kontaktformular kontaktieren, werden Ihre Angaben (Name, E-Mail, Nachricht) zur Bearbeitung Ihrer Anfrage gespeichert.
                  </p>
                  <p>
                    <strong className="text-white">Cookies:</strong> Unsere Website verwendet Cookies, um das Nutzererlebnis zu verbessern. Sie können Ihren Browser so einstellen, dass Sie über das Setzen von Cookies informiert werden.
                  </p>
                </div>
              </section>

              {/* Purpose */}
              <section className="bg-white/5 rounded-2xl border border-white/10 p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                    <Eye className="w-5 h-5 text-amber-400" />
                  </div>
                  <h2 className="font-display text-xl font-bold">Zweck der Datenverarbeitung</h2>
                </div>
                <ul className="space-y-3 text-slate-400 text-sm">
                  <li className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-teal-500 mt-2 flex-shrink-0" />
                    Bereitstellung und Optimierung unserer Website
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-teal-500 mt-2 flex-shrink-0" />
                    Beantwortung von Kontaktanfragen
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-teal-500 mt-2 flex-shrink-0" />
                    Analyse des Nutzerverhaltens zur Verbesserung unserer Dienste
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-teal-500 mt-2 flex-shrink-0" />
                    Erfüllung rechtlicher Verpflichtungen
                  </li>
                </ul>
              </section>

              {/* Security */}
              <section className="bg-white/5 rounded-2xl border border-white/10 p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
                    <Lock className="w-5 h-5 text-green-400" />
                  </div>
                  <h2 className="font-display text-xl font-bold">Datensicherheit</h2>
                </div>
                <p className="text-slate-400 text-sm leading-relaxed">
                  Wir setzen technische und organisatorische Sicherheitsmassnahmen ein, um Ihre Daten gegen zufällige oder vorsätzliche Manipulation, Verlust, Zerstörung oder gegen den Zugriff unberechtigter Personen zu schützen. Unsere Sicherheitsmassnahmen werden entsprechend der technologischen Entwicklung fortlaufend verbessert.
                </p>
              </section>

              {/* Rights */}
              <section className="bg-white/5 rounded-2xl border border-white/10 p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                    <UserCheck className="w-5 h-5 text-blue-400" />
                  </div>
                  <h2 className="font-display text-xl font-bold">Ihre Rechte</h2>
                </div>
                <p className="text-slate-400 text-sm mb-4">
                  Sie haben gemäss DSG und DSGVO folgende Rechte:
                </p>
                <ul className="space-y-3 text-slate-400 text-sm">
                  <li className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 flex-shrink-0" />
                    <span><strong className="text-white">Auskunftsrecht:</strong> Sie können Auskunft über Ihre gespeicherten Daten verlangen.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 flex-shrink-0" />
                    <span><strong className="text-white">Berichtigungsrecht:</strong> Sie können die Berichtigung unrichtiger Daten verlangen.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 flex-shrink-0" />
                    <span><strong className="text-white">Löschungsrecht:</strong> Sie können die Löschung Ihrer Daten verlangen.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 flex-shrink-0" />
                    <span><strong className="text-white">Widerspruchsrecht:</strong> Sie können der Datenverarbeitung widersprechen.</span>
                  </li>
                </ul>
              </section>

              {/* Contact */}
              <section className="bg-gradient-to-br from-teal-500/10 to-cyan-500/10 rounded-2xl border border-teal-500/20 p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Mail className="w-5 h-5 text-teal-400" />
                  <h2 className="font-display text-xl font-bold">Fragen zum Datenschutz?</h2>
                </div>
                <p className="text-slate-400 text-sm mb-4">
                  Bei Fragen zur Erhebung, Verarbeitung oder Nutzung Ihrer personenbezogenen Daten wenden Sie sich bitte an:
                </p>
                <a href="mailto:info@mysymp.ch" className="inline-flex items-center gap-2 text-teal-400 hover:text-teal-300 transition-colors">
                  info@mysymp.ch
                </a>
              </section>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-white/5 py-6">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <div className="flex items-center justify-between">
            <span className="text-slate-500 text-xs">© {new Date().getFullYear()} marKIpilot – MYSYMP AG</span>
            <div className="flex items-center gap-4">
              <Link to="/impressum" className="text-slate-500 hover:text-slate-300 text-xs">Impressum</Link>
              <Link to="/agb" className="text-slate-500 hover:text-slate-300 text-xs">AGB</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
